import { EXPENSE_PERIODS } from '@clockwise/shared';
import { z } from 'zod';

/**
 * `cost` is a string, not a number, and stays that way end to end.
 *
 * The column is `numeric(12,2)`; Postgres and Drizzle both surface it as a
 * decimal string so cents survive exactly. Parsing it into a JS number here
 * would reintroduce the binary-float rounding the column type exists to
 * avoid, and `Number('')` silently yields `0` — a missing cost would render
 * as a real $0.00 charge (G15). Format for display, don't coerce for storage.
 */
export const ExpenseCatalogSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  /** `null` exactly when `variable` is true — a variable item has no standing
   *  price. Render it as such; never `Number(cost)` or `cost ?? 0`, both of
   *  which turn "not known" into a real $0.00 charge (G15). */
  cost: z.string().nullable(),
  /** The item's amount is only known per occurrence, so it carries no price
   *  and cannot seed one onto a customer line. */
  variable: z.boolean(),
  period: z.enum(EXPENSE_PERIODS),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExpenseCatalog = z.infer<typeof ExpenseCatalogSchema>;

/**
 * Cost accepted on the wire as a number or a string. The API canonicalizes it
 * to a two-decimal string and REJECTS more than two decimal places rather
 * than rounding — see the backend `costAmount` validator.
 */
const cost = z.union([z.number(), z.string()]);

const ExpenseCatalogBody = z.object({
  name: z.string().min(1).max(120),
  /** Required. Blank/whitespace-only is rejected by the API — the column is
   *  NOT NULL, so there is no "no description" state to represent. */
  description: z.string().min(1).max(500),
  /**
   * Required for a fixed item, and rejected outright for a variable one — the
   * two fields are one fact and the API 400s on a body where they disagree.
   * Optional here rather than conditional because the pairing needs the
   * stored row to judge a PATCH, which only the server has.
   *
   * `null` on a PATCH means "drop the price", which is what flipping an item
   * to variable does; sending `variable: true` alone has the same effect.
   */
  cost: cost.nullable().optional(),
  /** Defaults to false server-side. */
  variable: z.boolean().optional(),
  period: z.enum(EXPENSE_PERIODS),
  active: z.boolean().optional(),
});

export const CreateExpenseCatalogInput = ExpenseCatalogBody;
export type CreateExpenseCatalogInput = z.infer<typeof CreateExpenseCatalogInput>;

export const PatchExpenseCatalogInput = ExpenseCatalogBody.partial();
export type PatchExpenseCatalogInput = z.infer<typeof PatchExpenseCatalogInput>;

export type ExpenseCatalogSortField = 'name' | 'cost' | 'period' | 'createdAt' | 'updatedAt';

export interface ListExpenseCatalogsQuery {
  q?: string;
  active?: boolean;
  period?: (typeof EXPENSE_PERIODS)[number];
  sortField?: ExpenseCatalogSortField;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Export query. Single source of truth so a new list filter automatically
 * participates in export by adding one optional field here (and the matching
 * field on the backend's `exportQuerySchema`). Subset of
 * `ListExpenseCatalogsQuery` minus pagination — exports drain the whole
 * filtered result.
 */
export type ExportExpenseCatalogsQuery = Pick<
  ListExpenseCatalogsQuery,
  'active' | 'period' | 'q' | 'sortField' | 'sortDirection'
>;

/** Result of POST /api/expense-catalogs/import — one entry per row in the
 *  uploaded file (in the same order). `outcome` reflects whether the row was
 *  created, updated by upsert-on-name, or rejected. */
export type ImportExpenseCatalogRowOutcome = 'created' | 'updated' | 'unchanged' | 'failed';

/**
 * Structured per-row error. `field` (when present) is the column the error
 * relates to; the UI can use it to anchor the message next to the offending
 * column header. `code` is a coarse machine-readable tag so future automation
 * can branch on it.
 */
export type ImportExpenseCatalogErrorCode =
  | 'REQUIRED'
  | 'INVALID_FORMAT'
  | 'TOO_LONG'
  | 'DUPLICATE_IN_FILE'
  | 'CONFLICT'
  | 'INTERNAL';

export interface ImportExpenseCatalogRowError {
  code: ImportExpenseCatalogErrorCode;
  message: string;
  field?: 'name' | 'description' | 'cost' | 'period' | 'variable' | 'active';
}

export interface ImportExpenseCatalogsRow {
  rowNumber: number;
  name: string | null;
  outcome: ImportExpenseCatalogRowOutcome;
  errors?: ImportExpenseCatalogRowError[];
}

export interface ImportExpenseCatalogsResult {
  totalRows: number;
  created: number;
  /** Rows that matched an existing item AND the file's values differed from
   *  on-disk; produced an audit entry. */
  updated: number;
  /** Rows that matched an existing item but were no-ops (file values equal
   *  on-disk values). No audit entry. Reported so re-importing an unedited
   *  export doesn't masquerade as N successful updates. */
  unchanged: number;
  failed: number;
  rows: ImportExpenseCatalogsRow[];
}
